/**
 * Utility functions for formatting data
 */

import { formatCurrency as formatCurrencyFromConstants } from '../constants/currency';

export const formatDate = (
  dateString: string | Date | null | undefined,
  format: "short" | "long" | "numeric" | "custom" = "short"
): string => {
  // Handle null, undefined, or empty string
  if (!dateString) return "";

  // Ensure we have a Date object
  let date: Date;
  if (dateString instanceof Date) {
    date = dateString;
  } else if (typeof dateString === "string") {
    date = new Date(dateString);
  } else {
    // Try to convert other types to Date
    date = new Date(dateString as any);
  }

  // Check if date is a Date instance and valid (Invalid Date objects return NaN for getTime())
  if (!(date instanceof Date)) return "";
  if (isNaN(date.getTime())) return "";

  switch (format) {
    case "short":
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    case "long":
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "long",
      });
    case "numeric":
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    case "custom":
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    default:
      return date.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
  }
};

export const formatDateTime = (dateString: string | Date | null | undefined): string => {
  // Handle null, undefined, or empty string
  if (!dateString) return "";

  // Ensure we have a Date object
  let date: Date;
  if (dateString instanceof Date) {
    date = dateString;
  } else if (typeof dateString === "string") {
    date = new Date(dateString);
  } else {
    // Try to convert other types to Date
    date = new Date(dateString as any);
  }

  // Check if date is a Date instance and valid (Invalid Date objects return NaN for getTime())
  if (!(date instanceof Date)) return "";
  if (isNaN(date.getTime())) return "";

  // Format date part
  const datePart = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  // Check if time is exactly midnight (00:00:00) - indicates no time was provided
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  
  if (hours === 0 && minutes === 0 && seconds === 0) {
    // No time was provided, show N/A
    return `${datePart}, N/A`;
  }

  // Format time part
  const timePart = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${datePart}, ${timePart}`;
};

export const formatTime = (dateString: string | Date | null | undefined): string => {
  // Handle null, undefined, or empty string
  if (!dateString) return "";

  // Ensure we have a Date object
  let date: Date;
  if (dateString instanceof Date) {
    date = dateString;
  } else if (typeof dateString === "string") {
    date = new Date(dateString);
  } else {
    // Try to convert other types to Date
    date = new Date(dateString as any);
  }

  // Check if date is a Date instance and valid (Invalid Date objects return NaN for getTime())
  if (!(date instanceof Date)) return "";
  if (isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const formatCurrency = (
  amount: number | string,
  currency?: string
): string => {
  // Use centralized currency formatting, ignore the currency parameter for consistency
  return formatCurrencyFromConstants(amount);
};

export const formatNumber = (
  num: number | string,
  decimals: number = 2
): string => {
  const numValue = typeof num === "string" ? parseFloat(num) : num;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numValue);
};

export const formatPercentage = (
  value: number,
  decimals: number = 1
): string => {
  return new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value / 100);
};

export const capitalizeWords = (str: string): string => {
  if (!str) return str;
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const formatStatusText = (status: string): string => {
  if (!status) return status;
  return capitalizeWords(status.replace('_', ' '));
};

// export const formatTo12Hour = (time24: string): string => {
//   // if (!time24) return time24;

//   // const [hoursStr, minutesStr] = time24.split(':');
//   // let hours = parseInt(hoursStr, 10);
//   // const minutes = minutesStr ? parseInt(minutesStr, 10) : 0;

//   // const ampm = hours >= 12 ? 'PM' : 'AM';
//   // hours = hours % 12 || 12; // convert 0 → 12 and 13–23 → 1–11

//   // return `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
//   if (!time24) return time24;

//   try {
//     // Parse the UTC time string
//     const date = new Date(time24);

//     // Convert to IST using toLocaleString with Asia/Kolkata timezone
//     const istTime = date.toLocaleString("en-IN", {
//       timeZone: "Asia/Kolkata",
//       hour: "2-digit",
//       minute: "2-digit",
//       hour12: true,
//     });

//     return istTime; // e.g. "10:50 AM"
//   } catch (error) {
//     console.error("Invalid date format:", time24);
//     return time24;
//   }
// };

export const formatTo12Hour = (timeUTC: string): string => {
  if (!timeUTC) return timeUTC;

  try {
    const date = new Date(timeUTC); // expects full ISO string like "2025-11-06T05:20:07.569Z"

    const istTime = date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return istTime; // e.g. "10:50 AM"
  } catch (error) {
    console.error("Invalid date format:", timeUTC);
    return timeUTC;
  }
};
